import { Directive, ElementRef, HostListener, Input, OnInit, inject } from '@angular/core';

@Directive({
  selector: 'input[matInput][type=number][howdenMax]',
  standalone: true
})
export class NumberMaxDirective implements OnInit {
  private el = inject<ElementRef<HTMLInputElement>>(ElementRef);

  @Input() howdenMax: number = 9_999_999;

  private lastValid = '';

  ngOnInit(): void {
    const input = this.el.nativeElement;
    input.setAttribute('max', String(this.howdenMax));
    this.lastValid = input.value ?? '';
  }

  @HostListener('beforeinput', ['$event'])
  onBeforeInput(e: InputEvent): void {
    if (e.inputType !== 'insertText' || typeof e.data !== 'string') { return; }
    if (!/^\d$/.test(e.data)) { return; }

    const input = this.el.nativeElement;
    const selStart = input.selectionStart ?? input.value.length;
    const selEnd = input.selectionEnd ?? selStart;

    const next
      = input.value.slice(0, selStart) + e.data + input.value.slice(selEnd);

    const [intPart] = next.split(/[.,]/);
    const intDigits = (intPart ?? '').replace(/\D/g, '').length;

    if (intDigits > this.maxIntegerDigits()) {
      e.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(_e: ClipboardEvent): void {
    setTimeout(() => this.validateAndRestore(), 0);
  }

  @HostListener('input')
  onInput(): void {
    this.validateAndRestore();
  }

  private validateAndRestore(): void {
    const input = this.el.nativeElement;
    const raw = (input.value ?? '').trim();

    if (raw === '') {
      this.lastValid = '';
      return;
    }

    const normalized = raw.replace(',', '.');

    const [intPart] = raw.split(/[.,]/);
    const intDigits = (intPart ?? '').replace(/\D/g, '').length;
    if (intDigits > this.maxIntegerDigits()) {
      this.restoreLastValid();
      return;
    }

    const n = Number(normalized);
    if (!Number.isFinite(n) || n < 0 || n > this.howdenMax) {
      this.restoreLastValid();
      return;
    }

    this.lastValid = input.value;
  }

  private restoreLastValid(): void {
    const input = this.el.nativeElement;
    input.value = this.lastValid;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  private maxIntegerDigits(): number {
    const digits = String(Math.trunc(this.howdenMax)).replace(/\D/g, '');
    return Math.max(1, digits.length);
  }
}
